import React, { useContext, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { SYSTEM_COLLAPSE, USER_COLLAPSE } from 'common/actions/row-groups/types';
import { DeleteRowButton } from 'components/LedgerTable/components';
import FeaturedSpreadsheetContext, { FeaturedSpreadsheetContextType } from '../context/FeaturedSpreadsheetContext';

// Define interfaces for the component props
interface Cell {
  parent?: string;
  [key: string]: any;
}

interface RowProps {
  cells: Cell[];
  children: React.ReactNode[];
  row: number;
  isLegend?: boolean;
}

const StyledRow = styled('tr')({
  '&:hover .delete-row': {
    opacity: 1,
  },
});

const StyledTableData = styled('td')({
  opacity: 0,
});

const Row: React.FC<RowProps> = ({ cells, children, row, isLegend }) => {
  const { rowGroups, displayLegend, isToolbarVisible, rows, displayDeleteRowBtn, deleteRowFn }
    = useContext<FeaturedSpreadsheetContextType>(FeaturedSpreadsheetContext);

  const isRowVisible = useMemo(() => {
    if (rowGroups) {
      // Look if any of the cells has a parent
      const result = cells.find(cell => cell?.parent);

      // Check if the collapsible rows has the parent
      if (result?.parent) {
        const rowGroup = rowGroups?.[result.parent];
        const isRowGroupCollapsed = rowGroup === SYSTEM_COLLAPSE || rowGroup === USER_COLLAPSE;

        if (isRowGroupCollapsed) {
          return false;
        }
      }
    }

    return !(!displayLegend && row === 0 && !isToolbarVisible);
  }, [rowGroups, cells, displayLegend, row, isToolbarVisible]);

  const visibleClass = useMemo(() => (isRowVisible ? 'visible' : 'hidden'), [isRowVisible]);

  // Check if this row is deletable (not virtual)
  const isRowDeletable = useMemo(() => {
    // Check if any cell in this row has isDeletableRow set to true
    // or if no cell has is_virtual set to true
    const firstCell = cells?.[0];
    if (firstCell) {
      // If isDeletableRow is explicitly set, use that
      if ('isDeletableRow' in firstCell) {
        return firstCell.isDeletableRow;
      }
      // Otherwise check if it's not virtual
      if ('is_virtual' in firstCell) {
        return !firstCell.is_virtual;
      }
    }
    // Default to true for backward compatibility
    return true;
  }, [cells]);

  return (
    <StyledRow className={`${visibleClass}`}>
      {children}
      {displayDeleteRowBtn && deleteRowFn && !isLegend && row > 1 && row <= rows.length && isRowDeletable && (
        <StyledTableData className="delete-row">
          <DeleteRowButton rowIndex={row - 1} deleteRow={deleteRowFn} />
        </StyledTableData>
      )}
    </StyledRow>
  );
};

export default Row;
